/**
 * ConversationsPage — /conversations. Every user-agent, agent-agent and team thread, on the `ListPage` template built
 * from the NextAdmin demo `/tables/data-tables`: toolbar (search, status pills, type filter), `DataTable`, load more.
 */
import { useInfiniteQuery } from '@tanstack/react-query';
import { AlertTriangle, MessageSquare, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Can } from '../../components/auth/Can.js';
import { ListPage } from '../../components/page/ListPage.js';
import { buttonStyles } from '../../components/tailgrids/core/button.js';
import {
  Badge,
  Button,
  Callout,
  Chip,
  DataTable,
  type DataTableColumn,
  EmptyState,
  FilterPills,
  SearchBar,
  Toolbar,
} from '../../components/ui/index.js';
import { listConversationsInfiniteOptions } from '../../generated/api/@tanstack/react-query.gen.js';
import type { Conversation } from '../../generated/api/types.gen.js';
import { PAGE_HELP } from '../../help/index.js';
import { relativeTime } from '../../utils/relative-time.js';
import { ConversationFilterSelects } from './conversations-list/ConversationFilterSelects.js';
import {
  type ConversationKind,
  KIND_LABEL,
  STATUS_LABEL,
  STATUS_VARIANT,
  subjectLabel,
  summarySnippet,
} from './conversations-list/conversation-labels.js';

type PillId = 'all' | 'active' | 'needs-you' | 'attention';

function buildPillOptions(rows: readonly Conversation[]) {
  return [
    { id: 'all', label: 'All', count: rows.length },
    { id: 'active', label: 'Active', count: rows.filter((row) => row.status === 'active').length },
    { id: 'needs-you', label: 'Waiting for you', count: rows.filter((row) => row.needsUser).length },
    { id: 'attention', label: 'Needs attention', count: rows.filter((row) => row.status === 'errored').length },
  ];
}

function getRowAttention(row: Conversation): 'danger' | 'warning' | undefined {
  if (row.status === 'errored') return 'danger';
  if (row.needsUser) return 'warning';
  return undefined;
}

const COLUMNS: DataTableColumn<Conversation>[] = [
  {
    key: 'topic',
    header: 'Conversation',
    width: '38%',
    render: (row) => {
      const snippet = summarySnippet(row.structuredSummary);
      return (
        <div className="min-w-0">
          <Link
            to={`/conversations/${row.id}`}
            onClick={(event) => event.stopPropagation()}
            className="block truncate font-semibold text-heading hover:text-accent-text"
          >
            {row.topic}
          </Link>
          {snippet && <p className="mt-0.5 line-clamp-1 max-w-[460px] text-xs text-muted">{snippet}</p>}
        </div>
      );
    },
  },
  {
    key: 'status',
    header: 'Status',
    render: (row) => (
      <div className="flex flex-wrap items-center gap-1.5">
        <Badge variant={STATUS_VARIANT[row.status]} dot>
          {STATUS_LABEL[row.status]}
        </Badge>
        {row.needsUser && <Badge variant="warning">Waiting for you</Badge>}
      </div>
    ),
  },
  {
    key: 'kind',
    header: 'Type',
    render: (row) => <Chip tone={row.kind === 'agent-agent' ? 'teal' : 'neutral'}>{KIND_LABEL[row.kind]}</Chip>,
  },
  {
    key: 'subject',
    header: 'About',
    render: (row) => (row.subjectType ? subjectLabel(row.subjectType) : <span className="text-faint">—</span>),
  },
  {
    key: 'lastActivityAt',
    header: 'Last activity',
    sortable: true,
    sortValue: (row) => row.lastActivityAt,
    render: (row) => <time dateTime={row.lastActivityAt}>{relativeTime(row.lastActivityAt)}</time>,
  },
];

export function ConversationsPage() {
  const help = PAGE_HELP['conversations'];
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [pill, setPill] = useState<PillId>('all');
  const [kindFilter, setKindFilter] = useState<ConversationKind | ''>('');

  const { data, isLoading, isError, isFetchingNextPage, hasNextPage, fetchNextPage, refetch } = useInfiniteQuery({
    ...listConversationsInfiniteOptions({
      query: {
        limit: 20,
        sort_by: 'lastActivityAt',
        sort_order: 'desc',
        ...(kindFilter ? { kind: kindFilter } : {}),
      },
    }),
    initialPageParam: { query: {} },
    getNextPageParam: (last) =>
      last.paging.hasMore && last.paging.cursors.after ? { query: { after: last.paging.cursors.after } } : undefined,
  });

  const allRows = useMemo(() => data?.pages.flatMap((page) => page.data) ?? [], [data]);

  const visible = useMemo(() => {
    let rows = allRows;
    if (pill === 'active') rows = rows.filter((row) => row.status === 'active');
    else if (pill === 'needs-you') rows = rows.filter((row) => row.needsUser);
    else if (pill === 'attention') rows = rows.filter((row) => row.status === 'errored');
    const query = search.trim().toLowerCase();
    if (!query) return rows;
    return rows.filter(
      (row) =>
        row.topic.toLowerCase().includes(query) ||
        (row.structuredSummary?.goals.some((goal) => goal.toLowerCase().includes(query)) ?? false),
    );
  }, [allRows, pill, search]);

  const newConversation = (
    <Can policies={['CanManageAgents', 'CanConverse']} mode="any">
      <Link to="/conversations/new" className={buttonStyles({ variant: 'primary', appearance: 'fill', size: 'sm' })}>
        <Plus size={14} aria-hidden />
        New conversation
      </Link>
    </Can>
  );

  const total = data?.pages[0]?.meta?.total ?? allRows.length;

  return (
    <ListPage
      title="Conversations"
      breadcrumbs={[{ label: 'AI Agents', to: '/agents/libraries' }]}
      subtitle="Every conversation with an agent, between agents and with your team. New requests are routed to a direct answer, a workflow, a task or a project."
      helpContent={help}
      actions={newConversation}
      alert={
        isError ? (
          <Callout
            tone="danger"
            icon={AlertTriangle}
            title="Could not load conversations"
            actions={
              <Button size="sm" variant="secondary" onClick={() => void refetch()}>
                Retry
              </Button>
            }
          >
            Check your connection and try again.
          </Callout>
        ) : undefined
      }
      toolbar={
        <Toolbar
          search={<SearchBar value={search} onChange={setSearch} placeholder="Search by topic or summary…" />}
          pills={
            <FilterPills
              aria-label="Status"
              options={buildPillOptions(allRows)}
              value={pill}
              onChange={(id) => setPill(id as PillId)}
            />
          }
          right={
            <ConversationFilterSelects
              kind={kindFilter}
              onKindChange={(value) => setKindFilter(value as ConversationKind | '')}
            />
          }
        />
      }
      footer={
        hasNextPage ? (
          <div className="flex justify-center">
            <Button variant="secondary" loading={isFetchingNextPage} onClick={() => void fetchNextPage()}>
              Load more
            </Button>
          </div>
        ) : undefined
      }
    >
      {!isError && (
        <DataTable
          columns={COLUMNS}
          rows={visible}
          rowKey={(row) => row.id}
          getRowAttention={getRowAttention}
          onRowClick={(row) => navigate(`/conversations/${row.id}`)}
          loading={isLoading}
          aria-label="Conversations"
          footer={visible.length > 0 ? `Showing ${visible.length} of ${total}, most recent first` : undefined}
          empty={
            allRows.length === 0 ? (
              <EmptyState
                icon={MessageSquare}
                title={help?.emptyState?.title ?? 'No conversations yet'}
                description={
                  help?.emptyState?.description ??
                  'Start a conversation and your request is routed to a direct answer, a task or a workflow.'
                }
                learnItems={help?.emptyState?.learnItems}
                action={newConversation}
              />
            ) : (
              <EmptyState
                icon={MessageSquare}
                title="No conversations match"
                description="Try a different status, type or search term."
              />
            )
          }
        />
      )}
    </ListPage>
  );
}
